import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import apiService from '../services/api';
import { useAuth } from './AuthContext';

const POLL_MS = 2500;

export const DOMAIN_TOPICS = {
  packages: 'packages',
  invoices: 'invoices',
  payments: 'payments',
  cobrancas: 'cobrancas',
  clients: 'clients',
  coletas: 'coletas',
  products: 'products',
  documents: 'documents',
  users: 'users',
  suppliers: 'suppliers',
  catalog: 'catalog',
  tracking: 'tracking',
  account_deletions: 'account_deletions',
};

/**
 * @typedef {{
 *   id: string,
 *   event: string,
 *   topic?: string,
 *   action?: string,
 *   payload?: Record<string, unknown>,
 *   at: number
 * }} DomainRealtimeEvent
 */

const RealtimeContext = createContext({
  subscribe: (_topics, _onEvent) => () => undefined,
  connected: false,
});

export const useRealtime = () => useContext(RealtimeContext);

export const RealtimeProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const listenersRef = useRef(new Set());
  const sinceRef = useRef(Date.now());
  const connectedRef = useRef(false);

  const subscribe = useCallback((topics, onEvent) => {
    const topicSet = new Set(
      (Array.isArray(topics) ? topics : [topics]).filter(Boolean)
    );
    const listener = { topicSet, onEvent };
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return undefined;

    let cancelled = false;

    const dispatch = (events) => {
      if (!Array.isArray(events) || events.length === 0) return;
      for (const event of events) {
        const at = Number(event?.at) || 0;
        if (at > sinceRef.current) sinceRef.current = at;
        const topic = event?.topic || String(event?.event || '').split(':')[0];
        listenersRef.current.forEach((listener) => {
          if (listener.topicSet.size === 0 || listener.topicSet.has(topic)) {
            try {
              listener.onEvent(event);
            } catch (err) {
              console.error('Realtime listener error', err);
            }
          }
        });
      }
    };

    const tick = async () => {
      if (cancelled || document.hidden) return;
      try {
        const data = await apiService.getDomainRealtimeStream({
          since: sinceRef.current,
        });
        if (cancelled) return;
        connectedRef.current = true;
        if (Array.isArray(data?.events) && data.events.length) {
          dispatch(data.events);
        }
        if (data?.serverTime) {
          sinceRef.current = Math.max(sinceRef.current, Number(data.serverTime) - 1);
        }
      } catch {
        connectedRef.current = false;
      }
    };

    tick();
    const id = window.setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (!document.hidden) tick();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(id);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated]);

  const value = useMemo(
    () => ({
      subscribe,
      connected: connectedRef.current,
    }),
    [subscribe]
  );

  return (
    <RealtimeContext.Provider value={value}>{children}</RealtimeContext.Provider>
  );
};
